import React from 'react';
import { Award, GraduationCap, ShieldCheck } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const certifications = [
  { name: 'AWS Certified Generative AI Developer – Professional', issuer: 'Amazon Web Services', date: 'Jan 2026', color: '#FF9900', highlight: true },
  { name: 'AWS Certified GenAI Developer – Professional (Early Adopter)', issuer: 'Amazon Web Services', date: 'Jan 2026', color: '#FF9900', highlight: true },
  { name: 'AWS Certified Solutions Architect', issuer: 'Amazon Web Services', date: 'May 2025', color: '#FF9900' },
  { name: 'AWS Certified AI Practitioner', issuer: 'Amazon Web Services', date: 'Mar 2025', color: '#FF9900' },
  { name: 'AWS Certified Cloud Practitioner', issuer: 'Amazon Web Services', date: 'Feb 2025', color: '#FF9900' },
  { name: 'MTA: Database Fundamentals', issuer: 'Microsoft', date: 'Apr 2018', color: '#00A4EF' },
  { name: 'MTA: Software Development Fundamentals', issuer: 'Microsoft', date: 'Apr 2018', color: '#00A4EF' },
  { name: 'Digital Advertising 101', issuer: 'Semrush', date: 'Nov 2024', color: '#FF622D' },
  { name: 'SEO Fundamentals', issuer: 'Semrush', date: 'Nov 2024', color: '#FF622D' },
];

const education = [
  { degree: 'M.S. Computing Systems', school: 'Georgia Institute of Technology', year: '2020' },
  { degree: 'B.S. Computer Science', school: 'University of Puerto Rico at Bayamón', year: '2016' },
];

const Certifications: React.FC = () => {
  return (
    <div>
      {/* ─── Hero ─── */}
      <section
        className="hero-fade-in hero-padding-lg"
        style={{
          background: 'linear-gradient(135deg, #fafafa, #f0f0f5)',
          borderRadius: '24px',
          marginBottom: '48px',
          textAlign: 'center',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '6px 16px',
            background: 'rgba(30,58,95,0.08)',
            color: 'var(--color-primary)',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginBottom: '16px',
          }}
        >
          <Award size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '6px' }} />
          Credentials
        </span>
        <h1
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 800,
            fontFamily: 'var(--font-heading)',
            marginBottom: '16px',
          }}
        >
          Certifications & Education
        </h1>
        <p
          style={{
            fontSize: '1.1rem',
            color: 'var(--color-gray-500)',
            lineHeight: 1.7,
            maxWidth: '560px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          9 professional certifications and 2 advanced degrees backing every solution I build.
        </p>
      </section>

      {/* ─── Education ─── */}
      <section style={{ marginBottom: '48px' }}>
        <AnimatedSection>
          <h2 className="text-3xl font-bold" style={{ marginBottom: '24px' }}>Education</h2>
        </AnimatedSection>
        <div className="grid-2-col">
          {education.map((edu, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(94,96,206,0.06), rgba(100,223,223,0.06))',
                  border: '1px solid rgba(94,96,206,0.12)',
                  borderRadius: '16px',
                  padding: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(94,96,206,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '14px',
                    background: 'rgba(94,96,206,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-primary)',
                    flexShrink: 0,
                  }}
                >
                  <GraduationCap size={22} />
                </div>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--color-gray-800)', marginBottom: '4px' }}>
                    {edu.degree}
                  </p>
                  <p style={{ fontSize: '0.88rem', color: 'var(--color-gray-500)' }}>
                    {edu.school} · {edu.year}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── Certifications ─── */}
      <section style={{ marginBottom: '64px' }}>
        <AnimatedSection>
          <h2 className="text-3xl font-bold" style={{ marginBottom: '24px' }}>Professional Certifications</h2>
        </AnimatedSection>
        <div
          className="grid-cards"
          style={{
            gap: '16px',
          }}
        >
          {certifications.map((cert, i) => (
            <AnimatedSection key={i} delay={i * 60}>
              <div
                style={{
                  background: '#fff',
                  borderRadius: '14px',
                  padding: '20px 24px',
                  border: (cert as any).highlight ? `1px solid ${cert.color}30` : '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                  position: 'relative' as const,
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)';
                }}
              >
                {(cert as any).highlight && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      background: `linear-gradient(90deg, ${cert.color}, ${cert.color}80)`,
                    }}
                  />
                )}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: `${cert.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ShieldCheck size={20} style={{ color: cert.color }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-gray-800)', marginBottom: '2px', lineHeight: 1.3 }}>
                    {cert.name}
                  </p>
                  <p style={{ fontSize: '0.78rem', color: 'var(--color-gray-400)' }}>
                    {cert.issuer} · {cert.date}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Certifications;
